import { Field, FormatNumber, Group, HStack, Input, Spinner, Text } from '@chakra-ui/react';
import { ChangeEvent, ReactElement, useCallback } from 'react';
import { RoundedButton } from './rounded-button';
import { InputGroup } from './ui/input-group';

const numericValueRegex = /^\d*(\.\d*)?$/;

export const InputCard = ({
  balance,
  startElement,
  endElement,
  value,
  onChange,
  loading,
  denom,
  disabled,
  header,
  subText,
}: {
  balance: number;
  value: string;
  onChange?: (n: string) => void;
  startElement?: ReactElement;
  endElement?: ReactElement;
  loading?: boolean;
  denom?: string;
  disabled?: boolean;
  header?: string;
  subText?: string;
}) => {
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!onChange) return;
    const valueAsNum = Number(e.target.value);
    if (!numericValueRegex.test(e.target.value) || isNaN(valueAsNum)) {
      return;
    }
    if (balance < valueAsNum) {
      onChange(balance.toString());
    } else {
      onChange(e.target.value);
    }
  };
  return (
    <Field.Root borderColor="border" borderWidth="thin" borderRadius="xl" p={5} mb={4} bgColor="bg">
      <Text fontSize="sm" color="fg.muted" fontWeight="500">
        {header}
      </Text>
      <InputGroup w="full" startElement={startElement} endElement={endElement} endElementProps={{ p: 0 }}>
        <Input
          disabled={disabled}
          border="none"
          fontWeight="500"
          placeholder="0"
          value={value}
          onChange={handleChange}
          px={0}
          fontSize="4xl"
          outline="none"
        />
      </InputGroup>
      {subText && subText}
      <Field.HelperText w="full" mt={8}>
        <HStack justifyContent="space-between">
          <Text>
            {loading ? <Spinner /> : balance} {denom} Available
          </Text>
          <Group>
            {onChange &&
              [0.25, 0.5, 1].map((percent) => (
                <SetAmountButton key={percent} percentToSet={percent} onChange={onChange} balance={balance} />
              ))}
          </Group>
        </HStack>
      </Field.HelperText>
    </Field.Root>
  );
};

const SetAmountButton = ({
  percentToSet,
  onChange,
  balance,
}: {
  percentToSet: number;
  balance: number;
  onChange: (e: string) => void;
}) => {
  const onClick = useCallback(() => {
    onChange(String(balance * percentToSet));
  }, [percentToSet, onChange, balance]);
  return (
    <RoundedButton h={26} color="fg.muted" bgColor="#F9F9F9" onClick={onClick} _hover={{ bg: '#F0F0F0' }}>
      {percentToSet === 1 ? 'MAX' : <FormatNumber value={percentToSet} style="percent" />}
    </RoundedButton>
  );
};
